import { useRef, useEffect, useState } from 'react';

export default function StorySection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-20 lg:py-28 overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #FFF8F0 0%, #FFF3E5 50%, #FFF0DC 100%)' }}>
      {/* Subtle jaali background */}
      <div
        className="absolute inset-0 opacity-4 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,107,53,0.25) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      ></div>

      {/* Side mural accent */}
      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-saffron via-coral to-teal"></div>
      <div className="absolute right-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-teal via-turmeric to-magenta"></div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text Side */}
          <div className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div className="relative pl-8">
              {/* Vertical accent line */}
              <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-saffron via-turmeric to-coral rounded-full"></div>

              <p className="font-sans text-xs uppercase tracking-[0.3em] text-saffron mb-4">Our Story</p>
              <h2 className="font-serif text-4xl sm:text-5xl text-charcoal leading-tight mb-6">
                Where Every Dish<br />
                <em className="text-saffron not-italic">Tells a Story</em>
              </h2>
              <p className="font-sans text-base text-charcoal/70 leading-relaxed mb-5">
                Chaska Indian Kitchen was born from a deep love for the vibrant street foods of India — the sizzling tawas of Delhi, the fragrant biryanis of Hyderabad, and the smoky tandoors of the Punjab. We brought that soul to Duncan, South Carolina, right here at 1675 E Main St.
              </p>
              <p className="font-sans text-base text-charcoal/70 leading-relaxed mb-8">
                Our kitchen is where tradition meets creativity. Every recipe carries the warmth of a grandmother&apos;s hand, the boldness of street-food culture, and the artistry of modern Indian cuisine. We don&apos;t just cook food — we craft experiences that stay with you long after the last bite.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 py-6 border-t border-b border-saffron/20">
                {[
                  { value: '100+', label: 'Menu Items', icon: 'ri-restaurant-2-line' },
                  { value: '4.9★', label: 'Google Rating', icon: 'ri-star-fill' },
                  { value: 'Fresh', label: 'Daily Prep', icon: 'ri-leaf-line' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center group">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-saffron/10 mx-auto mb-2">
                      <i className={`${stat.icon} text-saffron text-lg`}></i>
                    </div>
                    <p className="font-serif text-2xl text-saffron font-bold">{stat.value}</p>
                    <p className="font-sans text-xs text-charcoal/60 mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>

              <p className="font-accent text-xl text-coral mt-6">
                &ldquo;Authenticity is our ingredient. Passion is our recipe.&rdquo;
              </p>
            </div>
          </div>

          {/* Visual Side */}
          <div className={`relative transition-all duration-1000 delay-300 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            {/* Offset decorative frame — multi-color */}
            <div
              className="absolute -bottom-5 -right-5 w-full h-full rounded-3xl"
              style={{ background: 'linear-gradient(135deg, rgba(255,107,53,0.3), rgba(13,115,119,0.3))', border: '2px solid transparent' }}
            ></div>
            <div className="absolute -bottom-2 -right-2 w-full h-full rounded-3xl border-2 border-turmeric/30"></div>

            {/* Main Image */}
            <div className="relative rounded-3xl overflow-hidden" style={{ aspectRatio: '4/5' }}>
              <img
                src="https://readdy.ai/api/search-image?query=vibrant%20Indian%20restaurant%20interior%20wall%20featuring%20bold%20colorful%20hand%20painted%20mural%20art%20inspired%20by%20Indian%20truck%20art%20Bollywood%20poster%20style%20saffron%20orange%20teal%20magenta%20cultural%20folk%20painting%20artistic%20dining%20space%20rich%20vivid%20colors%20warm%20atmosphere%20professional%20photography&width=600&height=750&seq=1301&orientation=portrait"
                alt="Chaska Restaurant Interior Mural"
                className="w-full h-full object-cover object-top"
              />
              {/* Jaali overlay */}
              <div
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage: 'radial-gradient(circle, #FF6B35 1px, transparent 1px)',
                  backgroundSize: '16px 16px',
                }}
              ></div>
              {/* Bottom gradient */}
              <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/30 to-transparent"></div>
            </div>

            {/* Floating accent dots */}
            <div className="absolute -top-4 -left-4 w-8 h-8 rounded-full bg-saffron/40"></div>
            <div className="absolute -top-2 -left-8 w-4 h-4 rounded-full bg-coral/50"></div>
            <div className="absolute top-8 -left-6 w-3 h-3 rounded-full bg-turmeric/60"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
